/* Utils */
import { DrmKeySystem, ErrorType } from '../../constants';
import { getWebOSDevApi } from './general';

function createFailureCallback(callback) {
  return ({ errorCode, errorText }) => {
    const error = new Error(errorText);
    error.code = errorCode;
    callback(error);
  };
}

function loadDrm(drmAgent) {
  return new Promise((resolve, reject) => {
    if (!drmAgent) {
      // eslint-disable-next-line sonarjs/no-duplicate-string -- Doesn't matter
      reject(new Error('No drmAgent'));
    }

    drmAgent.load({
      onSuccess: resolve,
      onFailure: createFailureCallback(reject),
    });
  });
}

function unloadDrm(drmAgent) {
  return new Promise((resolve, reject) => {
    if (!drmAgent) {
      reject(new Error('No drmAgent'));
    }

    drmAgent.unload({
      onSuccess: resolve,
      onFailure: createFailureCallback(reject),
    });
  });
}

function isDrmLoaded(drmAgent) {
  return new Promise((resolve, reject) => {
    if (!drmAgent) {
      reject(new Error('No drmAgent'));
    }

    drmAgent.isLoaded({
      onSuccess: ({ loadStatus }) => resolve(loadStatus),
      onFailure: createFailureCallback(reject),
    });
  });
}

let loadedKeySystem;

export async function unloadDrmAgent() {
  const webOSDev = await getWebOSDevApi();
  const loadedDrmAgent = webOSDev.drmAgent(loadedKeySystem);
  unloadDrm(loadedDrmAgent);
}

export async function loadDrmAgent(keySystem = DrmKeySystem.WIDEVINE, onError = undefined) {
  const webOSDev = await getWebOSDevApi();
  const keySystemToUse = webOSDev.DRM.Type[keySystem];
  const otherKeySystem = keySystemToUse === webOSDev.DRM.Type.WIDEVINE
    ? webOSDev.DRM.Type.PLAYREADY : webOSDev.DRM.Type.WIDEVINE;

  const drmAgentToUse = webOSDev.drmAgent(keySystemToUse);
  let isLoaded = false;

  try {
    isLoaded = await isDrmLoaded(drmAgentToUse);
  } catch (error) {
    console.error('[webOSUtils] Error checking if DRM agent is loaded');
    console.error(error, error.code);
  }

  if (!isLoaded) {
    const otherDrmAgent = webOSDev.drmAgent(otherKeySystem);
    try {
      const isOtherLoaded = await isDrmLoaded(otherDrmAgent);

      if (isOtherLoaded) {
        await unloadDrm(otherDrmAgent);
      }
    } catch (error) {
      console.error('[webOSUtils] Error while unloading the previously loaded DRM Agent');
      console.error(error, error.code);
    }
  }

  try {
    if (!isLoaded) {
      await loadDrm(drmAgentToUse);
    }

    loadedKeySystem = keySystemToUse;

    return true;
  } catch (error) {
    console.error('[webOSUtils] Error loading DRM Agent', error);
    if (onError) {
      onError({
        code: error.code,
        message: error.message,
        keySystemToUse,
        type: ErrorType.LOAD_DRM_AGENT,
      });
    }
    return false;
  }
}
